/**
 * Pop-up configurable desde el editor de temas (sections/mx-popup.liquid), versión para Loft.
 *
 * Decide cuándo abrirse (retraso, scroll o intento de salida), respeta las fechas de la
 * campaña, la frecuencia por visitante y a quienes ya se suscribieron. Si hay varios
 * pop-ups activos en la página, se muestra solo uno por visita.
 */

const PREFIX = 'mx-popup:';

/** Bloqueo de scroll de la página mientras hay un pop-up modal abierto. */
const scrollLockOwners = new Set();
/** @param {Element} owner */
function lockScroll(owner) {
  scrollLockOwners.add(owner);
  document.documentElement.style.overflow = 'hidden';
}
/** @param {Element} owner */
function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  if (scrollLockOwners.size === 0) document.documentElement.style.overflow = '';
}
const SUBSCRIBED_KEY = `${PREFIX}subscribed`;
const SUBMITTED_KEY = `${PREFIX}submitted`;
const DAY_MS = 24 * 60 * 60 * 1000;

const storage = {
  /** @param {Storage} store @param {string} key */
  get(store, key) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {Storage} store @param {string} key @param {string} value */
  set(store, key, value) {
    try {
      store.setItem(key, value);
    } catch {
      // Modo privado o almacenamiento bloqueado: el pop-up igual funciona, solo sin memoria.
    }
  },
  /** @param {Storage} store @param {string} key */
  remove(store, key) {
    try {
      store.removeItem(key);
    } catch {}
  },
};

// Cualquier suscripción exitosa al newsletter (pop-up o pie de página) cuenta como suscrito.
if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
  storage.set(localStorage, SUBSCRIBED_KEY, '1');
}

// Pop-up desde el que se envió un formulario antes de esta carga de página (se consume una vez).
const submittedFrom = storage.get(sessionStorage, SUBMITTED_KEY);
storage.remove(sessionStorage, SUBMITTED_KEY);

/** Sección actualmente seleccionada en el editor de temas, para reabrir tras un re-render. */
let selectedInEditor = null;
/** Hay un pop-up abierto o programado para abrirse en esta página. */
let claimed = false;

class MxPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {Array<() => void>} */
  #cleanups = [];

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('cancel', this.#onCancel);
    for (const form of this.querySelectorAll('form')) {
      form.addEventListener('submit', () => storage.set(sessionStorage, SUBMITTED_KEY, this.sectionId));
    }

    if (this.hasAttribute('data-design-mode')) {
      this.#setupEditor();
      return;
    }

    if (this.#resumeAfterSubmit()) return;
    if (!this.#isEligible()) return;

    this.#arm();
  }

  disconnectedCallback() {
    this.#disarm();
    this.removeEventListener('click', this.#onClick);
    if (this.dialog?.open) this.#finishClose();
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get isModal() {
    return this.dataset.modal === 'true';
  }

  /** La clave cambia con las fechas, así una campaña nueva se muestra a todos otra vez. */
  get seenKey() {
    return `${PREFIX}seen:${this.sectionId}:${this.dataset.start || ''}:${this.dataset.end || ''}`;
  }

  #isEligible() {
    if (this.dataset.enabled !== 'true') return false;
    if (!isWithinDates(this.dataset.start, this.dataset.end)) return false;

    if (this.dataset.hideSubscribed === 'true' && storage.get(localStorage, SUBSCRIBED_KEY)) return false;

    const days = Number(this.dataset.frequency || 0);
    if (days > 0) {
      const seenAt = Number(storage.get(localStorage, this.seenKey) || 0);
      if (seenAt && Date.now() - seenAt < days * DAY_MS) return false;
    } else if (storage.get(sessionStorage, this.seenKey)) {
      return false;
    }

    return true;
  }

  /** Tras enviar el formulario del pop-up la página se recarga: lo reabrimos para mostrar el resultado. */
  #resumeAfterSubmit() {
    if (!submittedFrom || submittedFrom !== this.sectionId) return false;

    claimed = true;
    this.open();
    return true;
  }

  #arm() {
    if (claimed) return;
    claimed = true;

    const delayMs = Number(this.dataset.delay || 0) * 1000;
    const trigger = this.dataset.trigger;
    const canDetectExit = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (trigger === 'scroll') {
      this.#armScroll(Number(this.dataset.scroll || 40) / 100, delayMs);
    } else if (trigger === 'exit' && canDetectExit) {
      this.#armExit();
    } else {
      this.#armTimer(delayMs);
    }
  }

  /** @param {number} ms */
  #armTimer(ms) {
    const timer = window.setTimeout(() => this.open(), ms);
    this.#cleanups.push(() => window.clearTimeout(timer));
  }

  /** @param {number} ratio @param {number} fallbackMs */
  #armScroll(ratio, fallbackMs) {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;

    // Página demasiado corta para hacer scroll: usamos el tiempo de espera.
    if (scrollable <= 0) {
      this.#armTimer(fallbackMs);
      return;
    }

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= ratio) {
        this.#disarm();
        this.open();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    this.#cleanups.push(() => window.removeEventListener('scroll', onScroll));
  }

  #armExit() {
    /** @param {MouseEvent} event */
    const onMouseOut = (event) => {
      if (event.relatedTarget === null && event.clientY <= 0) {
        this.#disarm();
        this.open();
      }
    };

    document.addEventListener('mouseout', onMouseOut);
    this.#cleanups.push(() => document.removeEventListener('mouseout', onMouseOut));
  }

  #disarm() {
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  /**
   * Abre el pop-up. Fuera del editor, si hay otro diálogo abierto (carro, búsqueda),
   * espera a que se cierre para no tapar lo que el cliente está haciendo.
   * @param {{ force?: boolean }} [options]
   */
  open({ force = false } = {}) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    if (!force && document.querySelector('dialog[open]')) {
      this.#armTimer(3000);
      return;
    }

    if (!this.hasAttribute('data-design-mode')) {
      const days = Number(this.dataset.frequency || 0);
      storage.set(days > 0 ? localStorage : sessionStorage, this.seenKey, String(Date.now()));
    }

    dialog.classList.remove('is-closing');

    if (this.isModal) {
      lockScroll(dialog);
      dialog.showModal();
    } else {
      dialog.show();
      document.addEventListener('keydown', this.#onKeyDown);
    }
  }

  close() {
    const dialog = this.dialog;
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      this.#finishClose();
      return;
    }

    dialog.classList.add('is-closing');
    const done = () => {
      window.clearTimeout(fallback);
      dialog.removeEventListener('animationend', done);
      this.#finishClose();
    };
    const fallback = window.setTimeout(done, 400);
    dialog.addEventListener('animationend', done);
  }

  #finishClose() {
    const dialog = this.dialog;
    if (!dialog) return;

    dialog.classList.remove('is-closing');
    dialog.close();
    unlockScroll(dialog);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // Botón cerrar, o clic en el fondo oscuro (el propio <dialog>, fuera de la tarjeta).
    if (target.closest('[data-mx-close]') || target === this.dialog) {
      this.close();
      return;
    }

    // Un enlace dentro del pop-up que lleva a otra parte de la tienda.
    if (target.closest('a[href]')) this.close();
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  #setupEditor() {
    /** @param {Event & { detail?: { sectionId?: string } }} event */
    const onSelect = (event) => {
      const isThis =
        event.detail?.sectionId === this.sectionId || (event.target instanceof Node && this.contains(event.target));
      if (!isThis) return;
      selectedInEditor = this.sectionId;
      this.open({ force: true });
    };

    /** @param {Event & { detail?: { sectionId?: string } }} event */
    const onDeselect = (event) => {
      if (event.detail?.sectionId !== this.sectionId) return;
      selectedInEditor = null;
      this.close();
    };

    document.addEventListener('shopify:section:select', onSelect);
    document.addEventListener('shopify:block:select', onSelect);
    document.addEventListener('shopify:section:deselect', onDeselect);
    this.#cleanups.push(() => {
      document.removeEventListener('shopify:section:select', onSelect);
      document.removeEventListener('shopify:block:select', onSelect);
      document.removeEventListener('shopify:section:deselect', onDeselect);
    });

    // Al cambiar un ajuste, Shopify vuelve a dibujar la sección: la mantenemos abierta.
    if (selectedInEditor === this.sectionId) this.open({ force: true });
  }
}

/**
 * Fechas en formato AAAA-MM-DD, interpretadas en la hora local del visitante.
 * El día de término cuenta completo.
 * @param {string | undefined} start
 * @param {string | undefined} end
 */
function isWithinDates(start, end) {
  const now = Date.now();
  const startAt = parseDate(start);
  const endAt = parseDate(end);

  if (startAt !== null && now < startAt) return false;
  if (endAt !== null && now >= endAt + DAY_MS) return false;
  return true;
}

/** @param {string | undefined} value */
function parseDate(value) {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec((value || '').trim());
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
}

if (!customElements.get('mx-popup')) customElements.define('mx-popup', MxPopup);
